// The v1 FILTER BAR (brief §5.2): subject search, state, tag, needs-a-decision. Fully controlled —
// the surface owns the BoardFilterState and narrows the item slice client-side; the whole-board
// counts never change with the filter (that distinction is the projection's, not this bar's). Filter
// options derive from the projection (lifecycle states + the tags the slice actually carries) — the
// bar authors no vocabulary of its own (G-182).

import type { BoardFilterState } from '../board-model.ts';
import { stateLabel } from '../board-model.ts';

const FIELD =
  'h-8 rounded-sm border border-border bg-surface px-2.5 text-sm text-text hover:border-border-strong';

function FilterSelect({
  label,
  emptyLabel,
  value,
  options,
  onSelect,
}: {
  readonly label: string;
  readonly emptyLabel: string;
  readonly value: string | null;
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly onSelect: (value: string | null) => void;
}): React.JSX.Element {
  return (
    <select
      aria-label={label}
      value={value ?? ''}
      onChange={(event) => onSelect(event.target.value === '' ? null : event.target.value)}
      className={FIELD}
    >
      <option value="">{emptyLabel}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export interface BoardFilterBarProps {
  readonly states: readonly string[];
  readonly tags: readonly string[];
  readonly filter: BoardFilterState;
  readonly onFilterChange: (filter: BoardFilterState) => void;
}

/** The four v1 filters in one controlled row. */
export function BoardFilterBar({
  states,
  tags,
  filter,
  onFilterChange,
}: BoardFilterBarProps): React.JSX.Element {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <input
        type="search"
        aria-label="Search subjects"
        placeholder="Search subjects…"
        value={filter.subjectSearch}
        onChange={(event) => onFilterChange({ ...filter, subjectSearch: event.target.value })}
        className={`${FIELD} w-56 placeholder:text-text-subtle`}
      />
      <FilterSelect
        label="Filter by state"
        emptyLabel="All states"
        value={filter.state}
        options={states.map((state) => ({ value: state, label: stateLabel(state) }))}
        onSelect={(state) => onFilterChange({ ...filter, state })}
      />
      <FilterSelect
        label="Filter by tag"
        emptyLabel="All tags"
        value={filter.tag}
        options={tags.map((tag) => ({ value: tag, label: tag }))}
        onSelect={(tag) => onFilterChange({ ...filter, tag })}
      />
      <button
        type="button"
        aria-pressed={filter.needsDecision}
        onClick={() => onFilterChange({ ...filter, needsDecision: !filter.needsDecision })}
        className={
          'rounded-sm border px-3 py-1.5 text-sm font-medium transition-colors ' +
          (filter.needsDecision
            ? 'border-bracken bg-bracken-tint text-bracken'
            : 'border-border bg-surface text-text-muted hover:border-border-strong')
        }
      >
        Needs a decision
      </button>
    </div>
  );
}
